import { Component, DestroyRef, effect, inject, input, linkedSignal, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ExpansionPanel } from '@shared/components/expansion-panel/expansion-panel.component';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { Cartographie } from '../../../../../cartographie/models/cartographie.model';
import { ButtonAddNewComponent } from '../../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../../shared/components/toastr/toastr-success.component';
import { Phase } from '../../../processus-phases/models/phase.model';
import { PhaseStore } from '../../../processus-phases/store/phase.store';
import { Aptitude } from '../../models/aptitude.model';
import { AptitudeCreateRequest } from '../../requests/create-request';
import { AptitudeUpdateRequest } from '../../requests/update-request';
import { AptitudeStore } from '../../store/aptitude.store';

@Component({
  selector: 'app-form-aptitudes',
  imports: [
    ReactiveFormsModule,
    ButtonAddNewComponent,
    LabelRequiredDirective,
    HlmInputImports,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    ExpansionPanel,
    HlmInputGroupImports,
  ],
  templateUrl: './form-aptitudes.component.html',
})
export class FormAptitudesComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly phaseStore = inject(PhaseStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly phases = linkedSignal(() => this.phaseStore.entities());
  phase = input.required<Phase>();
  estCreation = input.required<boolean>();
  cartographie = input.required<Cartographie>();

  readonly form = this.#fb.group({
    aptitudes: this.#fb.array([]),
  });

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  constructor() {
    effect(() => {
      const phase = this.phase();
      if (phase && !this.estCreation()) {
        phase.aptitudes.forEach((aptitude) => {
          this.addAptitude(aptitude);
        });
      } else {
        this.addAptitude();
      }

      this.#aptitudeStore.charger({
        idCartographie: this.cartographie().id,
        idRéférentiel: this.phase().idReferentiel,
      });
    });
  }

  addAptitude(aptitude?: Aptitude) {
    const form = this.#fb.group({
      id: [aptitude?.id],
      indice: [aptitude?.indice],
      nom: [aptitude?.nom, [Validators.required]],
      phase: [this.phase(), [Validators.required]],
    });
    this.aptitudesForm.push(form);
  }

  onSubmit() {
    const toastrId = uuidv7();
    if (this.estCreation()) {
      const nouvellesAptitudes: AptitudeCreateRequest = {
        idReferentiel: this.phase().idReferentiel,
        aptitudes: this.aptitudesForm.value
          .filter((x: { nom: string; phase: Phase }) => !!x.nom?.trim())
          .map((x: { nom: string; phase: Phase }, idx: number) => ({
            nom: x.nom,
            indice: x.phase.aptitudes.length + idx,
            idPhase: x.phase.id,
          })),
      };

      if (!nouvellesAptitudes.aptitudes || nouvellesAptitudes.aptitudes.length === 0) {
        this.form.markAllAsTouched();
        return;
      }

      this.#aptitudeStore.créer({
        data: nouvellesAptitudes,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: 'Création des aptitudes réussie',
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
          this.phaseStore.charger(this.phase().idReferentiel);
        },
        error: () => this.alreadyExists(nouvellesAptitudes.aptitudes),
      });
    } else {
      const aptitudesModifiées: AptitudeUpdateRequest[] = this.aptitudesForm.value
        .filter((x: { nom: string }) => !!x.nom?.trim())
        .map((x: { nom: string; id: string; indice: number; phase: Phase }) => ({
          id: x.id,
          nom: x.nom,
          indice: x.indice,
          idPhase: x.phase.id,
          idReferentiel: this.phase().idReferentiel,
        }));

      if (aptitudesModifiées.length === 0) {
        this.form.markAllAsTouched();
        return;
      }

      if (this.alreadyExists(aptitudesModifiées)) return;

      for (var [idx, aptitude] of aptitudesModifiées.entries()) {
        var found = this.#aptitudeStore.entityMap()[aptitude.id];

        if (found && found.idPhase === aptitude.idPhase && found.nom === aptitude.nom) {
          // pour fermer le sidepanel quand on arrive au dernier indicateur à modifier, sinon reste ouvert
          if (idx === aptitudesModifiées.length - 1) {
            this.#sidepanelService.close();
          }
          continue;
        }

        this.#aptitudeStore.modifier({
          idAptitude: aptitude.id,
          data: aptitude,
          next: () => {
            this.#sidepanelService.close();
            this.phaseStore.charger(this.phase().idReferentiel);
          },
        });
      }
    }
  }

  alreadyExists(
    nouvellesAptitudes: {
      nom: string;
      idPhase: string;
      id?: string;
    }[],
  ) {
    let doublonsPrésents = false;

    const aptitudes = this.#aptitudeStore.entities().map((x) => ({
      nom: x.nom,
      idPhase: x.idPhase,
      id: x.id,
    }));

    let doublonsAvecAptitudesExistantes = aptitudes.filter(
      (x) =>
        nouvellesAptitudes.some((y) => y.idPhase === x.idPhase && y.nom === x.nom) &&
        // pour ne pas compter celles que nous n'avons pas modifié dans le sidepanel
        !nouvellesAptitudes.some((y) => y.idPhase === x.idPhase && y.nom === x.nom && y.id === x.id),
    );

    this.aptitudesForm.controls.forEach((control) => {
      if (
        doublonsAvecAptitudesExistantes.some((x) => x.nom === control.value.nom) ||
        this.aptitudesForm.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
        doublonsPrésents = true;
      }
    });

    this.aptitudesForm.markAllAsTouched();

    return doublonsPrésents;
  }

  get aptitudesForm() {
    return this.form.get('aptitudes') as FormArray<FormGroup>;
  }
}
